import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Palette, Lock, Image as ImageIcon, ArrowRight, Heart, Volume2, VolumeX } from 'lucide-react';

const Landing = () => {
  const [stats, setStats] = useState({ photos: 0, videos: 0 });
  const [albums, setAlbums] = useState([]);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    axios.get('/api/photos').then((res) => {
      const photos = res.data.filter((m) => m.type === 'image');
      const videos = res.data.filter((m) => m.type === 'video');
      setStats({ photos: photos.length, videos: videos.length });
      setAlbums(res.data.slice(0, 5));
    }).catch(() => {});
  }, []);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleVideoEnded = (e) => {
    e.target.play();
  };

  return (
    <div className="animate-fade-in pt-24">
      {/* ─── Hero Section ─── */}
      <section className="relative flex flex-col md:flex-row items-center justify-between gap-8 py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden rounded-3xl">
        {/* Background Video */}
        <div className="absolute inset-0 -z-20 w-full h-full overflow-hidden">
          <video
            ref={videoRef}
            className="w-full h-full object-cover opacity-20"
            src="/media/Cat_walking_and_waving_animation_202608180224.mp4"
            autoPlay
            loop
            playsInline
            muted
            onEnded={handleVideoEnded}
          />
        </div>

        {/* Sound Toggle */}
        <button
          onClick={toggleMute}
          className="absolute bottom-6 right-6 z-20 w-10 h-10 rounded-full bg-surface/80 backdrop-blur-md shadow-soft-card flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-200"
          aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
        >
          {isMuted ? (
            <VolumeX size={18} className="text-primary" />
          ) : (
            <Volume2 size={18} className="text-primary" />
          )}
        </button>

        {/* Decorative Background */}
        <div className="absolute -z-10 top-0 left-0 w-64 h-64 bg-primary-container/20 rounded-full blur-3xl" />

        {/* Left: Text */}
        <div className="w-full md:w-1/2 flex flex-col items-center md:items-start gap-4 text-center md:text-left z-10">
          <h1 className="text-headline-lg-mobile md:text-headline-xl font-bold text-primary leading-tight tracking-tight">
            Album Kenangan Sopia
          </h1>
          <p className="text-body-lg text-on-surface-variant max-w-md font-medium">
            Sanctuary pribadi Sopia untuk menyimpan setiap detik momen berharga. Ruang aman yang manis dan eksklusif untuk koleksi foto serta video kesayanganmu.
          </p>
          <Link
            to="/album"
            className="mt-2 text-label-md bg-primary-container text-on-primary-container px-8 py-4 rounded-full font-bold shadow-bubble hover:scale-95 active:scale-90 transition-all duration-200"
          >
            Buka Albumku
          </Link>
        </div>

        {/* Right: Floating Polaroids */}
        <div className="w-full md:w-1/2 relative flex justify-center mt-8 md:mt-0 z-10">
          {/* Main polaroid */}
          <div className="relative w-72 h-80 bg-surface rounded-xl p-4 shadow-soft-card rotate-[-6deg] hover:rotate-0 transition-transform duration-300">
            <img
              className="w-full h-full object-cover rounded-lg bg-surface-container-low"
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuDFOUZ-zz7U7ISUfM5kbBa7e3wHJg0gZ9Pm9v9BNoWeBfZ0xJFYKD07mqpuAVN064bgGgrO6Ov4BWXZXGlzs0Ye-0QLvOhEJSYI7WcAVWNTnIc0ZDz0Zmlcn5jCg-zRL-4POz8iJUNZWhfXdF5UzBcxsouudNUgSHtVyQFkdDLv4g3rvt-aRIpcHeVECSLvSW7Nbnj1NejKlesgzug0fz4drfEGKrYETS8G66jNqPeUQKf_S_mImu3yxw"
              alt="Samoyed dengan pita pink lucu"
            />
            {/* Washi tape accent */}
            <div className="absolute top-[-10px] left-1/2 transform -translate-x-1/2 w-16 h-6 bg-secondary-container/80 backdrop-blur-sm rotate-2 z-10" />
          </div>

          {/* Secondary polaroid */}
          <div className="absolute top-10 right-0 md:-right-10 w-48 h-56 bg-surface rounded-xl p-3 shadow-soft-card rotate-[8deg] hover:rotate-0 transition-transform duration-300 z-0">
            <img
              className="w-full h-full object-cover rounded-lg bg-surface-container-low"
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuA3AGEnAVfwQY-W_fqg0pkYEWy0bc97L9HOVrfs9iCVlzaMmn6aCMVrunvxMaRk72s2ahy3mN7FBJFWW6Cc20G45zN0ftj_Uur6tyz9x6nyo9RtjTl2-LdjTkV67dBzKN4KkZRYrGeZr1BzS7gs36mgrpaVLf65LXO5kOy0pC9JCUomLOdzL5OkKPLZx6V2qGSVVu0m8EhcCS8BOEHqpv1dC48I4LoREyVRiy7dORqJFbmrPkMVHOJeJg"
              alt="Macaron pastel di piring keramik"
            />
            <div className="absolute top-[-8px] right-4 w-12 h-4 bg-tertiary-container/80 backdrop-blur-sm -rotate-6 z-10" />
          </div>
        </div>
      </section>

      {/* ─── Features Section ─── */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="bg-surface-container-low rounded-3xl p-8 relative overflow-hidden">
          {/* Decorative Background */}
          <div className="absolute -z-10 bottom-0 right-0 w-96 h-96 bg-secondary-container/30 rounded-full blur-3xl" />

          <h2 className="text-headline-lg text-primary text-center mb-8 font-bold">
            Kenapa pilih fotoSOP?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-surface rounded-xl p-8 flex flex-col items-center text-center shadow-soft-card hover:-translate-y-2 transition-transform duration-300">
              <div className="w-16 h-16 bg-primary-container rounded-full flex items-center justify-center mb-4 shadow-bubble">
                <Palette size={28} className="text-primary" />
              </div>
              <h3 className="text-headline-md text-primary mb-2 font-bold">Desain Kesukaanku</h3>
              <p className="text-body-md text-on-surface-variant font-medium">
                Tampilan antarmuka yang lembut, manis, dan mudah digunakan oleh siapa saja.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-surface rounded-xl p-8 flex flex-col items-center text-center shadow-soft-card hover:-translate-y-2 transition-transform duration-300">
              <div className="w-16 h-16 bg-secondary-container rounded-full flex items-center justify-center mb-4 shadow-bubble">
                <Lock size={28} className="text-secondary" />
              </div>
              <h3 className="text-headline-md text-primary mb-2 font-bold">Aman & Eksklusif</h3>
              <p className="text-body-md text-on-surface-variant font-medium">
                Kenangan berhargamu tersimpan dengan aman, hanya bisa diakses oleh orang tersayang.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-surface rounded-xl p-8 flex flex-col items-center text-center shadow-soft-card hover:-translate-y-2 transition-transform duration-300">
              <div className="w-16 h-16 bg-tertiary-container rounded-full flex items-center justify-center mb-4 shadow-bubble">
                <ImageIcon size={28} className="text-tertiary" />
              </div>
              <h3 className="text-headline-md text-primary mb-2 font-bold">Koleksi Pribadi</h3>
              <p className="text-body-md text-on-surface-variant font-medium">
                Dukung upload foto dan video untuk mengabadikan momen secara utuh.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Gallery Preview (Bento Grid) ─── */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex justify-between items-end mb-6">
          <h2 className="text-headline-lg text-primary font-bold">Album Terbaru</h2>
          <Link
            to="/album"
            className="text-label-md text-secondary hover:text-primary transition-colors flex items-center gap-1 font-bold"
          >
            Buka Galeri Penuh <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-4 h-auto md:h-[600px]">
          {/* Large Feature Box */}
          <div className="md:col-span-2 md:row-span-2 bg-surface rounded-[2rem] p-3 shadow-soft-card group relative overflow-hidden">
            <img
              className="w-full h-full object-cover rounded-[1.5rem] group-hover:scale-105 transition-transform duration-700"
              src={albums[0]?.url || "https://lh3.googleusercontent.com/aida-public/AB6AXuCWqDJj2khMPu7UBhLMgKjA9L6-aehOmZiGV2eNUHIwxXNVAFKOABRBWZdS48ucu01U_tzZckY8TN8MMPafp1IptkUuHBPBPtKO1rXxSbq0wRXs4QRBBgnhRgShkEyeePIiKtNsw31TM4c9hvmsjmNuvJAa4V2D-306r0-_3nJTGKarx7bSdztL7Y2eW1-TURdRYrvMJmlT5IYbH7oEQZ9CtkwZz6mzcUJuXz4OyF5g0Hyw6CX986OZkA"}
              alt={albums[0]?.originalName || "Es krim stroberi"}
            />
            <div className="absolute bottom-6 left-6 right-6 bg-surface/80 backdrop-blur-md rounded-xl p-4 shadow-sm border border-surface-container-lowest/50">
              <h4 className="text-headline-md text-primary font-bold">{albums[0]?.originalName || "Summer Trip 2023"}</h4>
              <p className="text-label-sm text-on-surface-variant font-bold">
                {stats.photos} Photos • {stats.videos} Videos
              </p>
            </div>
          </div>

          {/* Small: Hot Chocolate */}
          <div className="md:col-span-1 md:row-span-1 bg-surface rounded-[2rem] p-2 shadow-soft-card group relative overflow-hidden">
            <img
              className="w-full h-full object-cover rounded-[1.5rem] group-hover:scale-105 transition-transform duration-700"
              src={albums[1]?.url || "https://lh3.googleusercontent.com/aida-public/AB6AXuCKeLCqIt55m6qqRKDogs5stERHLHhCASc2RO5ITAmDI6RvoWrWg8ClKJHVtD_UEzN319uNMeZO5dqsPHjnnDo7OXz5dSaqKwDPj4GnGFgfRRpet52wthCEUhxOzSkoJlY6bWyi4knw5Kc_nXWgkYd4lCqd3v_cfhrZVnmCFreYI92yrenfR1kd6uhPODFj93QSW6U8Rno55POX2M3qCS2suYWYkwqO4QZDfU9IuIfHe3mz9lSf5nAoVA"}
              alt={albums[1]?.originalName || "Cokelat panas dengan marshmallow"}
            />
          </div>

          {/* Small: Best Friends */}
          <div className="md:col-span-1 md:row-span-1 bg-surface-container-high rounded-[2rem] p-6 flex flex-col justify-center items-center text-center shadow-soft-card shadow-inner border border-surface-variant">
            <div className="w-12 h-12 bg-surface rounded-full flex items-center justify-center mb-2 shadow-sm text-primary">
              <Heart size={22} fill="currentColor" />
            </div>
            <p className="text-body-md text-primary font-bold">Best Friends</p>
          </div>

          {/* Wide: Vintage Camera */}
          <div className="md:col-span-2 md:row-span-1 bg-surface rounded-[2rem] p-2 shadow-soft-card group relative overflow-hidden h-48 md:h-auto">
            <img
              className="w-full h-full object-cover rounded-[1.5rem] group-hover:scale-105 transition-transform duration-700 object-center"
              src={albums[2]?.url || "https://lh3.googleusercontent.com/aida-public/AB6AXuBaz3C9BrpgWudPqaIg03nPCsNm-GjnZNa-zMIBAK9Cv2ZuFQOZMJ_lGH_bqbrSIDbm4byw1L2eN11LEbPMzhvH8jXAeG43WbNrZY2z2dXui4M3uG9ILc1rhPlB0lNtRgGi0cSlPD2S8IE-hSl24M5LQOP5_FDDjwHxus8jEzCxST3euTUacZMdjGZI8aDffC3cm1Od9TgPAZseTP0wTZqJ_Q0kws0VZ317ZL_zpcKRWsMgufF6XJ9PcA"}
              alt={albums[2]?.originalName || "Kamera vintage flat lay"}
            />
            <div className="absolute top-4 left-4 bg-secondary-container text-on-secondary-container px-3 py-1 rounded-full text-label-sm font-bold shadow-sm backdrop-blur-md">
              Vintage Vibe
            </div>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="w-full flex flex-col md:flex-row justify-between items-center px-6 py-8 mt-8 bg-surface-container rounded-t-lg">
        <div className="flex flex-col items-center md:items-start mb-6 md:mb-0">
          <span className="text-headline-md text-primary font-bold hover:opacity-80 transition-opacity">
            fotoSOP
          </span>
          <span className="text-label-md text-secondary mt-2 font-bold">
            Made with ♡ for your sweet memories
          </span>
        </div>
        <div className="flex gap-6">
          <span className="text-label-md text-on-secondary-container hover:text-primary hover:opacity-80 transition-opacity font-bold cursor-pointer">Privacy</span>
          <span className="text-label-md text-on-secondary-container hover:text-primary hover:opacity-80 transition-opacity font-bold cursor-pointer">Terms</span>
          <span className="text-label-md text-on-secondary-container hover:text-primary hover:opacity-80 transition-opacity font-bold cursor-pointer">Support</span>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
